import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Brain, Heart, Shield, Sparkles, ArrowRight, CheckCircle, Zap, Moon, Sun, Monitor,
  FileText, BarChart3, Bell, UserPlus, Video, Lock, ShieldCheck, X, ChevronLeft, ChevronRight,
  HelpCircle, Clock, Users, Menu
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useTheme } from "../lib/theme";
import InstallBanner from "../components/InstallBanner";

const DEMO_SLIDES = [
  {
    title: "Tableau de bord therapeute",
    desc: "Vue d'ensemble de vos patients, exercices assignes et progression en temps reel.",
    screenshot: "/demo-dashboard.jpg",
  },
  {
    title: "Gestion des patients",
    desc: "Gerez vos patients, invitez-les via un code unique et suivez leur statut.",
    screenshot: "/demo-patients.jpg",
  },
  {
    title: "Comptes rendus de seance",
    desc: "Generez et structurez vos comptes rendus assistes par IA. Export PDF integre.",
    screenshot: "/demo-reports.jpg",
  },
  {
    title: "Suivi patient detaille",
    desc: "Visualisez l'evolution de l'humeur, les exercices completes et les emotions les plus frequentes.",
    screenshot: "/demo-progress.jpg",
  },
];

const DemoModal = ({ open, onClose }) => {
  const [slide, setSlide] = useState(0);
  if (!open) return null;
  const s = DEMO_SLIDES[slide];
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-card rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="demo-modal">
        {/* Screenshot */}
        <div className="relative bg-muted">
          <button onClick={onClose} className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/40 hover:bg-black/60 text-white transition" data-testid="close-demo-modal">
            <X className="w-4 h-4" />
          </button>
          <img
            src={s.screenshot}
            alt={s.title}
            className="w-full h-auto max-h-[60vh] object-contain"
            data-testid={`demo-screenshot-${slide}`}
          />
        </div>
        {/* Caption + Nav */}
        <div className="p-5">
          <h3 className="font-heading text-lg font-semibold mb-1">{s.title}</h3>
          <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{s.desc}</p>
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSlide((slide - 1 + DEMO_SLIDES.length) % DEMO_SLIDES.length)}
              className="p-2 rounded-lg hover:bg-muted transition"
              data-testid="demo-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex gap-1.5">
              {DEMO_SLIDES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setSlide(i)}
                  className={`w-2 h-2 rounded-full transition-all ${i === slide ? "bg-primary w-6" : "bg-muted-foreground/30"}`}
                  data-testid={`demo-dot-${i}`}
                />
              ))}
            </div>
            <button
              onClick={() => setSlide((slide + 1) % DEMO_SLIDES.length)}
              className="p-2 rounded-lg hover:bg-muted transition"
              data-testid="demo-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { t, locale, setLocale } = useI18n();
  const { theme, cycleTheme } = useTheme();
  const [demoOpen, setDemoOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const FEATURES = [
    { icon: FileText, title: "CR assistes par IA", desc: "Generez et structurez vos comptes rendus a partir de vos notes, selon votre trame." },
    { icon: BarChart3, title: "Suivi patient", desc: "Visualisez la progression et les exercices realises entre les seances." },
    { icon: Bell, title: "Rappels intelligents", desc: "Si un exercice n'est pas fait, un rappel e-mail peut etre envoye automatiquement." },
    { icon: UserPlus, title: "Invitation ultra simple", desc: "Le patient rejoint votre suivi via un code unique lie a votre compte." },
    { icon: Video, title: "Contenus personnalises", desc: "Ajoutez vos propres videos (meditations guidees, relaxation)." },
    { icon: ShieldCheck, title: "Securite", desc: "Hebergement HDS + double authentification (2FA) therapeute." },
  ];

  const FAQ = [
    { q: "C'est par patient ?", a: "Non, par praticien. Patients illimites inclus dans l'abonnement." },
    { q: "Essai gratuit ?", a: "15 jours d'essai complet, sans carte bancaire." },
    { q: "Securite des donnees ?", a: "Hebergement certifie HDS + 2FA obligatoire pour les therapeutes. Consentements et charte de secret professionnel integres." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />

      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 shrink-0">
            <img src="/logo-kintsugi.png" alt="Kintsugi TCC" className="h-10 sm:h-12 w-auto" />
            <span className="font-heading text-lg sm:text-xl font-semibold leading-tight hidden sm:inline">Kintsugi TCC</span>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#fonctionnalites" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Fonctionnalites</a>
            <a href="#securite" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Securite</a>
            <a href="#tarifs" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Tarifs</a>
          </nav>
          <div className="hidden md:flex items-center gap-2 sm:gap-3">
            <button onClick={cycleTheme} className="text-xs text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-md border border-border" data-testid="theme-toggle-landing">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : theme === "light" ? <Moon className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
            </button>
            <button onClick={() => { const next = locale === "fr" ? "en" : locale === "en" ? "es" : "fr"; setLocale(next); }} className="text-xs text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-md border border-border" data-testid="lang-toggle">
              {locale === "fr" ? "EN" : locale === "en" ? "ES" : "FR"}
            </button>
            <Button variant="ghost" onClick={() => navigate("/login")} data-testid="login-btn" className="text-sm">
              {t("auth.login")}
            </Button>
            <Button onClick={() => navigate("/register")} data-testid="register-btn" className="bg-primary hover:bg-primary/90 text-sm">
              Essayer 15 jours
            </Button>
          </div>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 rounded-lg hover:bg-muted transition" data-testid="mobile-menu-toggle">
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-md px-4 py-4 space-y-3 animate-fade-in" data-testid="mobile-menu">
            <a href="#fonctionnalites" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-muted-foreground hover:text-foreground py-2">Fonctionnalites</a>
            <a href="#securite" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-muted-foreground hover:text-foreground py-2">Securite</a>
            <a href="#tarifs" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-muted-foreground hover:text-foreground py-2">Tarifs</a>
            <hr className="border-border" />
            <div className="flex items-center gap-2 py-1">
              <button onClick={cycleTheme} className="text-xs text-muted-foreground hover:text-foreground p-1.5 rounded-md border border-border">
                {theme === "dark" ? <Sun className="w-4 h-4" /> : theme === "light" ? <Moon className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
              </button>
              <button onClick={() => { const next = locale === "fr" ? "en" : locale === "en" ? "es" : "fr"; setLocale(next); }} className="text-xs text-muted-foreground hover:text-foreground p-1.5 rounded-md border border-border">
                {locale === "fr" ? "EN" : locale === "en" ? "ES" : "FR"}
              </button>
            </div>
            <Button variant="ghost" onClick={() => { navigate("/login"); setMobileMenuOpen(false); }} className="w-full justify-center text-sm">
              {t("auth.login")}
            </Button>
            <Button onClick={() => { navigate("/register"); setMobileMenuOpen(false); }} className="w-full justify-center bg-primary hover:bg-primary/90 text-sm">
              Essayer 15 jours
            </Button>
          </div>
        )}
      </header>

      {/* ============ HERO ============ */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-[3.4rem] font-bold text-foreground tracking-tight mb-6 leading-[1.15]">
                CR assistes par IA + suivi patient entre les seances
                <span className="text-primary"> — pour les praticiens liberaux</span>
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground mb-6 leading-relaxed">
                Assignez des exercices, suivez la progression, relancez automatiquement si besoin, et structurez vos comptes rendus.
                <br />
                <span className="font-medium text-foreground/80">Cadre securise : HDS + 2FA therapeutes.</span>
              </p>
              {/* Badges */}
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  { icon: Clock, label: "15 jours d'essai" },
                  { icon: Users, label: "Patients illimites" },
                  { icon: CheckCircle, label: "Sans cout par patient" },
                ].map((badge, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium" data-testid={`hero-badge-${i}`}>
                    <badge.icon className="w-3.5 h-3.5" />
                    {badge.label}
                  </span>
                ))}
              </div>
              {/* CTAs */}
              <div className="flex flex-wrap gap-4">
                <Button size="lg" onClick={() => navigate("/register")} data-testid="hero-cta-btn" className="bg-primary hover:bg-primary/90 gap-2">
                  Essayer 15 jours <ArrowRight className="w-4 h-4" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => setDemoOpen(true)} data-testid="hero-demo-btn">
                  Voir la demo
                </Button>
              </div>
            </div>
            <div className="relative animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img src="https://images.unsplash.com/photo-1724082519783-7a32899d294e" alt="Therapist workspace" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-card rounded-xl shadow-lg p-4 animate-slide-in border border-border" style={{ animationDelay: "0.4s" }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Suivi en continu</p>
                    <p className="text-xs text-muted-foreground">15+ exercices TCC</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ KINTSUGI SECTION (kept as-is) ============ */}
      <section className="py-24 px-6 relative overflow-hidden" data-testid="kintsugi-section">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-50/40 via-amber-100/20 to-transparent dark:from-amber-950/20 dark:via-amber-900/10 dark:to-transparent pointer-events-none" />
        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-16 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              {t("landing.kintsugi.sectionTitle")}
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl sm:text-4xl font-semibold mb-4 text-foreground">
              {t("landing.kintsugi.subtitle")}
            </h2>
          </div>
          <div className="grid lg:grid-cols-5 gap-12 items-center mb-16">
            <div className="lg:col-span-2 flex justify-center animate-fade-in" style={{ animationDelay: "0.1s" }}>
              <div className="relative w-64 h-64 sm:w-72 sm:h-72">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-200/60 via-amber-300/30 to-amber-100/20 dark:from-amber-800/30 dark:via-amber-700/15 dark:to-amber-900/10" />
                <div className="absolute inset-3 rounded-full bg-card border border-amber-200/60 dark:border-amber-700/40 flex items-center justify-center shadow-lg">
                  <img src="/logo-kintsugi.png" alt="Kintsugi TCC" className="w-40 h-40 sm:w-44 sm:h-44 object-contain" />
                </div>
                <div className="absolute top-6 right-4 w-12 h-0.5 bg-gradient-to-r from-amber-400 to-transparent rotate-[30deg]" />
                <div className="absolute bottom-10 left-2 w-16 h-0.5 bg-gradient-to-l from-amber-400 to-transparent rotate-[-20deg]" />
                <div className="absolute top-1/2 right-0 w-10 h-0.5 bg-gradient-to-r from-amber-400 to-transparent rotate-[10deg]" />
              </div>
            </div>
            <div className="lg:col-span-3 space-y-6 animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <p className="text-base text-muted-foreground leading-relaxed">{t("landing.kintsugi.paragraph1")}</p>
              <p className="text-base text-muted-foreground leading-relaxed">{t("landing.kintsugi.paragraph2")}</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-8 animate-fade-in" style={{ animationDelay: "0.3s" }}>
            {[
              { icon: Heart, title: t("landing.kintsugi.value1Title"), desc: t("landing.kintsugi.value1Desc"), color: "text-rose-500 dark:text-rose-400", bg: "bg-rose-50 dark:bg-rose-950/30" },
              { icon: Shield, title: t("landing.kintsugi.value2Title"), desc: t("landing.kintsugi.value2Desc"), color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-50 dark:bg-amber-950/30" },
              { icon: Zap, title: t("landing.kintsugi.value3Title"), desc: t("landing.kintsugi.value3Desc"), color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-950/30" },
            ].map((item, i) => (
              <div key={i} className="text-center p-6 rounded-xl bg-card border border-border/50 shadow-card" data-testid={`kintsugi-value-${i + 1}`}>
                <div className={`w-12 h-12 rounded-full ${item.bg} flex items-center justify-center mx-auto mb-4`}>
                  <item.icon className={`w-6 h-6 ${item.color}`} />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ POUR LES PRATICIENS LIBERAUX ============ */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-card rounded-2xl border border-border p-8 sm:p-10 shadow-card" data-testid="praticiens-section">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold mb-8 text-center">
              Pour les praticiens liberaux
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "Moins de temps sur l'administratif : CR structures avec l'IA",
                "Plus de continuite : suivi patient entre les seances",
                "Moins d'oublis : rappels d'exercices par e-mail si non realises",
                "Mise en place simple : invitation patient par code unique therapeute",
                "Personnalisable : upload de vos videos guidees",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg" data-testid={`praticien-benefit-${i}`}>
                  <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ FONCTIONNALITES CLES ============ */}
      <section id="fonctionnalites" className="py-20 px-6 bg-muted/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-heading text-2xl sm:text-3xl sm:text-4xl font-semibold mb-4">Fonctionnalites cles</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Tout ce dont vous avez besoin pour accompagner vos patients entre les seances.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feat, i) => (
              <Card key={i} className="bg-card shadow-card card-hover" data-testid={`feature-card-${i}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <feat.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold mb-2">{feat.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{feat.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECURITE & CADRE PROFESSIONNEL ============ */}
      <section id="securite" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-heading text-2xl sm:text-3xl sm:text-4xl font-semibold mb-4">Securite & cadre professionnel</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Vos donnees et celles de vos patients sont protegees selon les standards les plus exigeants.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { icon: Shield, title: "Hebergement certifie HDS", desc: "Vos donnees de sante sont hebergees sur une infrastructure certifiee Hebergeur de Donnees de Sante." },
              { icon: Lock, title: "2FA obligatoire therapeutes", desc: "Double authentification (Google Authenticator / Authy) requise a chaque connexion therapeute." },
              { icon: FileText, title: "Consentements & charte", desc: "Consentements RGPD en place. Charte du secret professionnel signee a l'inscription therapeute." },
              { icon: HelpCircle, title: "Outil d'accompagnement", desc: "Kintsugi TCC est un outil d'accompagnement therapeutique. Il ne remplace pas un suivi professionnel." },
            ].map((item, i) => (
              <div key={i} className="flex gap-4 p-5 rounded-xl bg-card border border-border shadow-card" data-testid={`security-item-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <item.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TARIFS ============ */}
      <section id="tarifs" className="py-20 px-6 bg-muted/50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-heading text-2xl sm:text-3xl sm:text-4xl font-semibold mb-4">Tarifs</h2>
            <p className="text-muted-foreground">Un seul abonnement praticien. Patients illimites.</p>
          </div>

          {/* Pricing cards */}
          <div className="grid sm:grid-cols-2 gap-6 mb-12">
            <Card className="bg-card shadow-card border-2 border-primary/20 relative overflow-hidden" data-testid="pricing-monthly">
              <CardContent className="p-8">
                <p className="text-sm font-medium text-muted-foreground mb-2">Mensuel</p>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-bold">49</span>
                  <span className="text-lg text-muted-foreground">EUR TTC / mois</span>
                </div>
                <p className="text-xs text-muted-foreground mb-6">Engagement 12 mois</p>
                <ul className="space-y-2.5 mb-8">
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-primary shrink-0" /> Patients illimites</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-primary shrink-0" /> CR assistes par IA</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-primary shrink-0" /> Rappels automatiques</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-primary shrink-0" /> 15+ exercices TCC</li>
                </ul>
                <Button className="w-full" onClick={() => navigate("/register")} data-testid="pricing-monthly-cta">Essayer 15 jours</Button>
              </CardContent>
            </Card>

            <Card className="bg-card shadow-card border-2 border-primary relative overflow-hidden" data-testid="pricing-annual">
              <div className="absolute top-0 right-0 bg-primary text-white text-xs font-bold px-3 py-1 rounded-bl-lg">1 mois offert</div>
              <CardContent className="p-8">
                <p className="text-sm font-medium text-muted-foreground mb-2">Annuel</p>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-bold">539</span>
                  <span className="text-lg text-muted-foreground">EUR TTC / an</span>
                </div>
                <p className="text-xs text-primary font-medium mb-6">Soit ~45 EUR/mois — 1 mois offert</p>
                <ul className="space-y-2.5 mb-8">
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-primary shrink-0" /> Tout le plan mensuel</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-primary shrink-0" /> 1 mois offert</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-primary shrink-0" /> Support prioritaire</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-primary shrink-0" /> Upload videos illimite</li>
                </ul>
                <Button className="w-full bg-primary hover:bg-primary/90" onClick={() => navigate("/register")} data-testid="pricing-annual-cta">Essayer 15 jours</Button>
              </CardContent>
            </Card>
          </div>

          <p className="text-center text-sm text-muted-foreground mb-10">
            15 jours d'essai gratuit. Sans carte bancaire. Sans engagement initial.
          </p>

          {/* Mini-FAQ */}
          <div className="space-y-4" data-testid="mini-faq">
            {FAQ.map((item, i) => (
              <div key={i} className="bg-card rounded-xl border border-border p-5" data-testid={`faq-${i}`}>
                <p className="font-medium text-sm mb-1 flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-primary shrink-0" />
                  {item.q}
                </p>
                <p className="text-sm text-muted-foreground ml-6">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-20 px-6 bg-primary text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl sm:text-3xl sm:text-4xl font-semibold mb-4">
            Pret a simplifier votre pratique ?
          </h2>
          <p className="text-white/80 mb-8">
            Rejoignez les praticiens qui gagnent du temps sur l'administratif et renforcent le suivi patient.
          </p>
          <Button
            size="lg"
            variant="secondary"
            onClick={() => navigate("/register")}
            data-testid="cta-register-btn"
            className="bg-white text-primary hover:bg-white/90 gap-2"
          >
            Essayer 15 jours <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo-kintsugi.png" alt="Kintsugi TCC" className="h-10 w-auto" />
            <span className="font-heading font-semibold">Kintsugi TCC</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-privacy-link">
              Politique de confidentialite
            </Link>
            <Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-terms-link">
              Conditions d'utilisation
            </Link>
            <Link to="/cgv" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-cgv-link">
              CGV
            </Link>
            <Link to="/charter" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-charter-link">
              Charte du secret professionnel
            </Link>
          </div>
          <p className="text-sm text-muted-foreground">{t("landing.footer")}</p>
        </div>
      </footer>
      <InstallBanner variant="therapist-landing" />
    </div>
  );
};

export default LandingPage;
